import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Globe, LogOut, Wallet, FileCheck, ArrowUpDown, MessageSquare } from 'lucide-react';
import TransactionHistory from '@/components/user/TransactionHistory';
import KYCUpload from '@/components/user/KYCUpload';
import DepositWithdraw from '@/components/user/DepositWithdraw';
import ChatSupport from '@/components/user/ChatSupport';
import { formatCurrency } from '@/lib/currencies';
import { supabase } from '@/integrations/supabase/client';

export default function UserDashboard() {
  const { profile, signOut } = useAuth();
  const { t, toggleLanguage, language } = useLanguage();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [freshProfile, setFreshProfile] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Re-fetch profile on mount to ensure fresh data
  useEffect(() => {
    const fetchFreshProfile = async () => {
      if (!profile?.id) return;
      
      setIsLoading(true);
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', profile.id)
        .single();
      
      if (!error && data) {
        console.log('🔥 Fresh profile fetched from DB:', data);
        console.log('🔥 Currency from DB:', data.currency);
        setFreshProfile(data);
      }
      setIsLoading(false);
    };
    
    fetchFreshProfile();

    // Set up real-time subscription
    const channel = supabase
      .channel(`profile_updates_${profile?.id}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'profiles',
        filter: `id=eq.${profile?.id}`
      }, (payload) => {
        console.log('🔥 Real-time profile update:', payload.new);
        setFreshProfile(payload.new);
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [profile?.id]);

  const displayProfile = freshProfile || profile;

  console.log('===== PROFILE DEBUG =====');
  console.log('freshProfile:', freshProfile);
  console.log('profile from context:', profile);
  console.log('displayProfile:', displayProfile);
  console.log('displayProfile.currency:', displayProfile?.currency);
  console.log('========================');

  if (isLoading || !displayProfile) return <div className="p-8">Loading...</div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      {/* Header */}
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold">Broker Platform</h1>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={toggleLanguage}>
              <Globe className="h-5 w-5" />
            </Button>
            <Button variant="outline" onClick={signOut}>
              <LogOut className="h-4 w-4 mr-2" />
              {t('logout')}
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 lg:w-fit">
            <TabsTrigger value="dashboard">
              <Wallet className="h-4 w-4 mr-2" />
              {t('dashboard')}
            </TabsTrigger>
            <TabsTrigger value="kyc">
              <FileCheck className="h-4 w-4 mr-2" />
              {t('kyc')}
            </TabsTrigger>
            <TabsTrigger value="transactions">
              <ArrowUpDown className="h-4 w-4 mr-2" />
              {t('transactions')}
            </TabsTrigger>
            <TabsTrigger value="chat">
              <MessageSquare className="h-4 w-4 mr-2" />
              {t('chat')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>{t('balance')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-bold text-primary">
                    {(() => {
                      const curr = displayProfile.currency || 'USD';
                      console.log('💰 Formatting balance:', { 
                        balance: displayProfile.balance, 
                        currency: curr, 
                        rawCurrency: displayProfile.currency 
                      });
                      return formatCurrency(Number(displayProfile.balance), curr, language);
                    })()}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Debug: Currency = "{displayProfile.currency}" | Balance = {displayProfile.balance}
                  </div>
                  <div className="mt-2 text-sm text-muted-foreground">
                    {t('kycStatus')}: <span className={`font-semibold ${
                      displayProfile.kyc_status === 'approved' ? 'text-green-600' :
                      displayProfile.kyc_status === 'rejected' ? 'text-red-600' :
                      'text-yellow-600'
                    }`}>{t(displayProfile.kyc_status)}</span>
                  </div>
                </CardContent>
              </Card>

              <DepositWithdraw />
            </div>

            <TransactionHistory userId={displayProfile.id} />
          </TabsContent>

          <TabsContent value="kyc">
            <KYCUpload userId={displayProfile.id} currentStatus={displayProfile.kyc_status} />
          </TabsContent>

          <TabsContent value="transactions">
            <TransactionHistory userId={displayProfile.id} showAll />
          </TabsContent>

          <TabsContent value="chat">
            <ChatSupport userId={displayProfile.id} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}